import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { Shield, Upload, Building2, FileText, CheckCircle, ChevronRight, ChevronLeft, Globe, MapPin } from 'lucide-react';

export default function SupplierRegister() {
  const { t } = useTranslation();
  const { user } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    companyName: '',
    companyRegistration: '',
    taxId: '',
    businessType: 'llc',
    yearEstablished: '',
    website: '',
    description: '',
    countryCode: 'US',
    city: '',
    pickupAvailable: false,
    pickupAddress: '',
    authorizationDoc: null as File | null,
    businessRegDoc: null as File | null,
    taxDoc: null as File | null,
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: string, value: string | boolean | File | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2 font-display">Application Submitted!</h2>
          <p className="text-[#64748b] mb-6">
            Your supplier application has been received. Our team will review your documents and verify your business within 3-5 business days. You'll receive an email notification once approved.
          </p>
          <Link to="/" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[800px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Become a Supplier</h1>
          <p className="text-[#64748b]">Register your business and start selling on IPO</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${s <= step ? 'bg-[#c2410c] text-white' : 'bg-gray-200 text-[#64748b]'}`}>
                {s < step ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              <div className={`flex-1 h-1 rounded-full ${s < step ? 'bg-[#c2410c]' : 'bg-gray-200'}`} />
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-[#1e293b] mb-1">Company Information</h2>
                <p className="text-sm text-[#64748b]">Tell us about your business</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Company Name *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <input type="text" value={form.companyName} onChange={(e) => handleChange('companyName', e.target.value)} placeholder="Acme Corporation" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Registration Number *</label>
                  <input type="text" value={form.companyRegistration} onChange={(e) => handleChange('companyRegistration', e.target.value)} placeholder="e.g., B-12345678" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Tax ID / VAT *</label>
                  <input type="text" value={form.taxId} onChange={(e) => handleChange('taxId', e.target.value)} placeholder="e.g., ES12345678" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Business Type</label>
                  <select value={form.businessType} onChange={(e) => handleChange('businessType', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] outline-none text-sm">
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="partnership">Partnership</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                    <option value="cooperative">Cooperative</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Year Established</label>
                  <input type="number" value={form.yearEstablished} onChange={(e) => handleChange('yearEstablished', e.target.value)} placeholder="2015" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Website</label>
                  <input type="text" value={form.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://yourcompany.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Company Description</label>
                  <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe your business, products, and services..." rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm resize-none" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-[#1e293b] mb-1">Location & Operations</h2>
                <p className="text-sm text-[#64748b]">Where you operate and how you deliver</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Country *</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <select value={form.countryCode} onChange={(e) => handleChange('countryCode', e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] outline-none text-sm">
                      <option value="US">United States</option>
                      <option value="SG">Singapore</option>
                      <option value="GB">United Kingdom</option>
                      <option value="FR">France</option>
                      <option value="DE">Germany</option>
                      <option value="ES">Spain</option>
                      <option value="AE">UAE</option>
                      <option value="KE">Kenya</option>
                      <option value="BR">Brazil</option>
                      <option value="CN">China</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e293b] mb-1.5">City *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                    <input type="text" value={form.city} onChange={(e) => handleChange('city', e.target.value)} placeholder="New York" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-[#1e293b] cursor-pointer">
                    <input type="checkbox" checked={form.pickupAvailable} onChange={(e) => handleChange('pickupAvailable', e.target.checked)} className="rounded border-gray-300" />
                    We offer pickup from our location
                  </label>
                </div>
                {form.pickupAvailable && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Pickup Address</label>
                    <textarea value={form.pickupAddress} onChange={(e) => handleChange('pickupAddress', e.target.value)} placeholder="Full pickup address..." rows={2} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm resize-none" />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-[#1e293b] mb-1">Document Verification</h2>
                <p className="text-sm text-[#64748b]">Upload required documents for verification via Veriff</p>
              </div>

              <div className="space-y-4">
                <div className="p-5 border-2 border-dashed border-gray-200 rounded-2xl hover:border-[#c2410c]/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#4338ca]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-[#1e293b]">Authorization Document *</h4>
                      <p className="text-xs text-[#64748b]">Board resolution or authorization letter</p>
                    </div>
                    <label className="px-4 py-2 bg-[#f8fafc] text-[#1e293b] rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Upload
                      <input type="file" className="hidden" onChange={(e) => handleChange('authorizationDoc', e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  {form.authorizationDoc && (
                    <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {form.authorizationDoc.name}
                    </p>
                  )}
                </div>

                <div className="p-5 border-2 border-dashed border-gray-200 rounded-2xl hover:border-[#c2410c]/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#4338ca]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-[#1e293b]">Business Registration *</h4>
                      <p className="text-xs text-[#64748b]">Valid business registration certificate</p>
                    </div>
                    <label className="px-4 py-2 bg-[#f8fafc] text-[#1e293b] rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Upload
                      <input type="file" className="hidden" onChange={(e) => handleChange('businessRegDoc', e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  {form.businessRegDoc && (
                    <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {form.businessRegDoc.name}
                    </p>
                  )}
                </div>

                <div className="p-5 border-2 border-dashed border-gray-200 rounded-2xl hover:border-[#c2410c]/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#4338ca]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-[#1e293b]">Tax Documents *</h4>
                      <p className="text-xs text-[#64748b]">Tax certificate or VAT registration</p>
                    </div>
                    <label className="px-4 py-2 bg-[#f8fafc] text-[#1e293b] rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Upload
                      <input type="file" className="hidden" onChange={(e) => handleChange('taxDoc', e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  {form.taxDoc && (
                    <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {form.taxDoc.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Your documents will be verified through our Veriff integration. The verification process typically takes 3-5 business days. All documents are encrypted and stored securely.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-[#64748b] hover:border-[#c2410c] hover:text-[#c2410c] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#c2410c] text-white rounded-xl text-sm font-medium hover:bg-[#9a3412] transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#c2410c] text-white rounded-xl text-sm font-medium hover:bg-[#9a3412] transition-colors"
              >
                Submit Application <CheckCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
